import { useState, useEffect, useCallback } from "react";
import { useGetDevicesQuery } from "../store/trader-api";

export interface DeviceStatus {
  isBound: boolean;
  isHealthy: boolean;
  deviceId?: string;
  deviceInfo?: {
    platform: string;
    model: string;
    lastSeenAt?: Date;
    authStatus: string;
  };
  loading: boolean;
  error?: string;
  refreshStatus?: () => void;
}

interface UseDeviceStatusOptions {
  holderId: string;
  pollingInterval?: number; // в миллисекундах
  enabled?: boolean;
}

export const useDeviceStatus = ({
  holderId,
  pollingInterval = 30000, // 30 секунд по умолчанию
  enabled = true,
}: UseDeviceStatusOptions): DeviceStatus => {
  const [deviceStatus, setDeviceStatus] = useState<DeviceStatus>({
    isBound: false,
    isHealthy: false,
    loading: true,
  });

  // Запрос устройств для данного держателя
  const {
    data: devicesResponse,
    isLoading,
    error,
    refetch,
  } = useGetDevicesQuery(
    { holderId },
    {
      skip: !enabled || !holderId,
      pollingInterval: enabled ? pollingInterval : 0,
    }
  );

  // Функция для принудительного обновления статуса
  const refreshStatus = useCallback(() => {
    refetch();
  }, [refetch]);

  // Обновляем статус при изменении данных
  useEffect(() => {
    if (isLoading) {
      setDeviceStatus((prev) => ({ ...prev, loading: true }));
      return;
    }

    if (error) {
      setDeviceStatus({
        isBound: false,
        isHealthy: false,
        loading: false,
        error: "Ошибка загрузки статуса устройства",
        refreshStatus,
      });
      return;
    }

    const devices = devicesResponse?.data || [];
    const activeDevice = devices.find((device) => device.isActive);
    const inactiveDevice = devices.find((device) => !device.isActive);
    const anyDevice = activeDevice || inactiveDevice;

    // Отладочная информация
    console.log(`[DeviceStatus] Holder ${holderId}: found ${devices.length} devices, active: ${!!activeDevice}, inactive: ${!!inactiveDevice}`);

    if (!anyDevice) {
      setDeviceStatus({
        isBound: false,
        isHealthy: false,
        loading: false,
        refreshStatus,
      });
      return;
    }

    // Определяем статус здоровья
    const deviceAge = Date.now() - new Date(anyDevice.createdAt).getTime();
    const isRecentlyBound = deviceAge < 5 * 60 * 1000; // 5 минут
    const hasNoHealthData = !anyDevice.health;
    
    // Для активного устройства проверяем все флаги здоровья
    // Для неактивного устройства - считаем нездоровым (проблемы)
    let isHealthy = false;
    
    if (activeDevice) {
      // Если нет данных о здоровье и устройство недавно привязано - считаем здоровым
      if (hasNoHealthData && isRecentlyBound) {
        isHealthy = true;
      } 
      // Если есть данные о здоровье - проверяем все флаги
      else if (activeDevice.health) {
        const health = activeDevice.health;
        isHealthy = 
          health.permissionsOk !== false &&
          health.notificationsOk !== false &&
          health.batteryOptimizationDisabled !== false &&
          health.backgroundServiceRunning !== false &&
          health.networkOk !== false &&
          health.airplaneMode !== true;

        // Check if device is online (seen in last 10 minutes)
        if (isHealthy && activeDevice.lastSeenAt) {
          const lastSeenTime = new Date(activeDevice.lastSeenAt).getTime();
          const isOnline = Date.now() - lastSeenTime < 10 * 60 * 1000; // 10 minutes
          if (!isOnline) {
            isHealthy = false;
          }
        }
      }
      // Если нет данных о здоровье и устройство не новое - нездоровое
      else {
        isHealthy = false;
      }
    } else {
      // Неактивное устройство = проблемы
      isHealthy = false;
    }

    setDeviceStatus({
      isBound: !!activeDevice, // Привязанным считается только активное устройство
      isHealthy,
      deviceId: anyDevice.id,
      deviceInfo: {
        platform: anyDevice.platform,
        model: anyDevice.model,
        lastSeenAt: anyDevice.lastSeenAt,
        authStatus: anyDevice.authStatus,
      },
      loading: false,
      refreshStatus,
    });
  }, [devicesResponse, isLoading, error, refreshStatus]);

  return {
    ...deviceStatus,
    refreshStatus,
  };
};

// Hook для поллинга статуса во время привязки
export const useDeviceBindingPolling = (
  holderId: string,
  isPolling: boolean,
  onSuccess?: () => void,
  onTimeout?: () => void,
  timeoutMs: number = 120000 // 2 минуты
) => {
  const [pollingStartTime, setPollingStartTime] = useState<number | null>(null);
  const [hasSucceeded, setHasSucceeded] = useState(false);

  const deviceStatus = useDeviceStatus({
    holderId,
    pollingInterval: 2000, // 2 секунды
    enabled: isPolling && !hasSucceeded,
  });

  // Устанавливаем время начала polling'а
  useEffect(() => {
    if (isPolling && !pollingStartTime) {
      setPollingStartTime(Date.now());
    }
    if (!isPolling) {
      setPollingStartTime(null);
      setHasSucceeded(false);
    }
  }, [isPolling, pollingStartTime]);

  useEffect(() => {
    if (!isPolling || hasSucceeded || !pollingStartTime) return;

    // Проверяем таймаут
    const elapsed = Date.now() - pollingStartTime;
    console.log(`[DevicePolling] Holder ${holderId}: elapsed ${Math.round(elapsed/1000)}s, isBound: ${deviceStatus.isBound}, isHealthy: ${deviceStatus.isHealthy}`);
    
    if (elapsed > timeoutMs) {
      console.log(`[DevicePolling] Timeout reached for holder ${holderId}`);
      onTimeout?.();
      return;
    }

    // Проверяем успешную привязку (достаточно просто isBound)
    if (deviceStatus.isBound) {
      console.log(`[DevicePolling] Device bound successfully for holder ${holderId}`);
      setHasSucceeded(true);
      onSuccess?.();
    }
  }, [
    deviceStatus.isBound,
    deviceStatus.isHealthy,
    isPolling,
    hasSucceeded,
    pollingStartTime,
    timeoutMs,
    onSuccess,
    onTimeout,
    holderId,
  ]);

  const timeRemaining = pollingStartTime 
    ? Math.max(0, timeoutMs - (Date.now() - pollingStartTime))
    : timeoutMs;

  return {
    ...deviceStatus,
    timeRemaining,
    hasSucceeded,
  };
};
